import React, { useEffect, useMemo, useState } from "react";
import NavigationHeader from "../../components/ui/NavigationHeader";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Select from "../../components/ui/Select";
import Icon from "../../components/AppIcon";
import { baseFilterOptions } from "../../utils/baseDisplay";
import { fetchCommercialEgestorOverview, fetchCommercialSnapshot } from "../comercial-dashboard/services/comercialApi";

const firstDayOfMonthISO = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`;
};
const todayISO = () => new Date().toISOString().slice(0, 10);
const formatCurrency = (value) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(value || 0));
const formatNumber = (value, digits = 0) => new Intl.NumberFormat("pt-BR", { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(Number(value || 0));
const formatDateBR = (value) => {
  if (!value) return "---";
  const [year, month, day] = String(value).slice(0, 10).split("-");
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
};
const escapePrintText = (value) => String(value ?? "---").replace(/[&<>"']/g, (char) => ({
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  "\"": "&quot;",
  "'": "&#39;",
}[char]));
const getAveragePrice = (row = {}) => {
  const returnedAverage = Number(row.precoMedio);
  if (Number.isFinite(returnedAverage) && returnedAverage > 0) return returnedAverage;
  const quantity = Number(row.quantidade || 0);
  return quantity > 0 ? Number(row.valorTotal || 0) / quantity : 0;
};

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-[#f7efe2] text-[#3b1d0f]">
      <NavigationHeader />
      <main className="px-4 pb-10 pt-24 sm:px-6 lg:px-8">{children}</main>
    </div>
  );
}

function MetricCard({ label, value, helper, icon = "BarChart3" }) {
  return (
    <div className="rounded-2xl border border-[#ead8c2] bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-[#7a5c46]">{label}</div>
          <div className="mt-2 text-3xl font-bold text-[#3b1d0f]">{value}</div>
          {helper ? <div className="mt-2 text-sm text-[#9a806c]">{helper}</div> : null}
        </div>
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#fff3df] text-[#d97706]">
          <Icon name={icon} size={18} />
        </div>
      </div>
    </div>
  );
}

function Panel({ title, subtitle, icon = "LayoutDashboard", children }) {
  return (
    <section className="rounded-[24px] border border-[#ead8c2] bg-white shadow-sm overflow-hidden">
      <div className="border-b border-[#efe2d1] px-6 py-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#fff3df] text-[#d97706]">
            <Icon name={icon} size={18} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-[#3b1d0f]">{title}</h2>
            {subtitle ? <p className="mt-1 text-sm text-[#7a5c46]">{subtitle}</p> : null}
          </div>
        </div>
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}

function SimpleTable({ rows, columns, emptyMessage = "Nenhum dado encontrado." }) {
  if (!rows?.length) {
    return <div className="rounded-2xl border border-dashed border-[#ead8c2] bg-[#fffaf4] px-4 py-8 text-center text-sm text-[#7a5c46]">{emptyMessage}</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-[#efe2d1] text-left text-[#8b6a52]">
            {columns.map((column) => <th key={column.key} className="px-3 py-3 font-semibold">{column.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id || row.key || row.codigo || index} className="border-b border-[#f4eadf] last:border-b-0">
              {columns.map((column) => (
                <td key={column.key} className="px-3 py-3 align-top text-[#4f3426]">
                  {column.render ? column.render(row) : row[column.key] ?? "---"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const initialOverview = {
  summary: {},
  productSummary: [],
  sellerSummary: [],
  topClients: [],
  warnings: [],
};

export default function VisaoGeralDashboard() {
  const [overviewFilters, setOverviewFilters] = useState({ dtIni: firstDayOfMonthISO(), dtFim: todayISO(), base: "ALL", limit: "1000" });
  const [overview, setOverview] = useState(initialOverview);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [error, setError] = useState("");
  const [overviewError, setOverviewError] = useState("");

  const loadOverview = async () => {
    setOverviewLoading(true);
    setOverviewError("");
    try {
      const data = await fetchCommercialEgestorOverview(overviewFilters);
      setOverview({
        summary: data?.summary || {},
        productSummary: Array.isArray(data?.productSummary) ? data.productSummary : [],
        sellerSummary: Array.isArray(data?.sellerSummary) ? data.sellerSummary : [],
        topClients: Array.isArray(data?.topClients) ? data.topClients : [],
        warnings: Array.isArray(data?.warnings) ? data.warnings : [],
      });
    } catch (err) {
      setOverviewError(err?.message || "Não foi possível carregar a visão geral.");
    } finally {
      setOverviewLoading(false);
    }
  };

  const loadSnapshot = async () => {
    try {
      const data = await fetchCommercialSnapshot();
      setSnapshot(data);
    } catch (err) {
      setError(err?.message || "Não foi possível carregar os indicadores gerais.");
    }
  };

  const generateOverviewReport = () => {
    if (overviewLoading) {
      window.alert("Aguarde os KPIs do eGestor terminarem de carregar antes de gerar o relatório.");
      return;
    }

    const reportWindow = window.open("", "_blank", "width=1500,height=900");
    if (!reportWindow) {
      window.alert("O navegador bloqueou a abertura do relatório. Libere os pop-ups desta página e tente novamente.");
      return;
    }

    const summary = overview?.summary || {};
    const companyLabel = baseFilterOptions.find((option) => option.value === overviewFilters.base)?.label || "Todas as empresas";
    const generatedAt = new Date().toLocaleString("pt-BR");
    const metricCards = [
      ["Produtos vendidos", formatNumber(summary.produtosVendidosTotal || 0), "Quantidade total vendida"],
      ["Pedidos feitos", formatNumber(summary.quantidadePedidos || 0), "Pedidos válidos no período"],
      ["Faturamento", formatCurrency(summary.faturamentoTotal || 0), "Sem cancelados"],
      ["Clientes ativos", formatNumber(summary.clientesAtivos45Dias || 0), "Compraram nos últimos 45 dias"],
      ["Clientes positivados", formatNumber(summary.clientesPositivadosMes || 0), "Compraram no período"],
      ["Ticket médio", formatCurrency(summary.ticketMedioCompra || 0), "Faturamento / pedidos"],
    ].map(([label, value, helper]) => `
      <div class="metric-card">
        <div class="metric-label">${escapePrintText(label)}</div>
        <div class="metric-value">${escapePrintText(value)}</div>
        <div class="metric-helper">${escapePrintText(helper)}</div>
      </div>`).join("");

    const productRows = (overview.productSummary || []).map((row) => `
      <tr>
        <td>${escapePrintText(row.produto)}</td>
        <td class="num">${escapePrintText(formatNumber(row.quantidade))}</td>
        <td class="num">${escapePrintText(formatCurrency(row.valorTotal))}</td>
        <td class="num">${escapePrintText(formatCurrency(getAveragePrice(row)))}</td>
      </tr>`).join("") || '<tr><td colspan="4" class="empty">Nenhum produto vendido no período.</td></tr>';

    const sellerRows = (overview.sellerSummary || []).map((row) => `
      <tr>
        <td>${escapePrintText(row.vendedor)}</td>
        <td class="num">${escapePrintText(formatNumber(row.pedidos))}</td>
        <td class="num">${escapePrintText(formatCurrency(row.faturamento))}</td>
        <td class="num">${escapePrintText(formatCurrency(row.ticketMedio))}</td>
      </tr>`).join("") || '<tr><td colspan="4" class="empty">Nenhum vendedor encontrado no período.</td></tr>';

    const clientRows = (overview.topClients || []).map((row) => `
      <tr>
        <td>${escapePrintText(row.cliente)}</td>
        <td class="num">${escapePrintText(formatNumber(row.pedidos))}</td>
        <td class="num">${escapePrintText(formatCurrency(row.faturamento))}</td>
        <td class="num">${escapePrintText(formatDateBR(row.ultimaCompra))}</td>
      </tr>`).join("") || '<tr><td colspan="4" class="empty">Nenhum cliente encontrado no período.</td></tr>';

    reportWindow.document.write(`<!doctype html>
      <html lang="pt-BR">
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>Relatório da visão geral comercial</title>
          <style>
            * { box-sizing: border-box; }
            html, body { background: #f7efe2; }
            body { margin: 0; padding: 18px; font-family: Arial, sans-serif; color: #3b1d0f; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .report { max-width: 1500px; margin: 0 auto; overflow: hidden; border: 1px solid #ead8c2; border-radius: 18px; background: #fff; }
            .header { padding: 16px 18px; border-bottom: 1px solid #f2e6d7; }
            h1 { margin: 0; font-size: 20px; }
            .subtitle { margin-top: 4px; color: #7a5c46; font-size: 11px; }
            .content { padding: 16px; }
            .filters { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
            .filter, .metric-card, .table-card { border: 1px solid #f0ddc7; border-radius: 12px; background: #fffaf4; }
            .filter { padding: 9px 11px; }
            .filter-label, .metric-label { color: #8b6a52; font-size: 9px; font-weight: 700; text-transform: uppercase; }
            .filter-value { margin-top: 5px; font-size: 12px; font-weight: 700; }
            .metrics { display: grid; grid-template-columns: repeat(6, 1fr); gap: 10px; margin-top: 12px; }
            .metric-card { min-height: 86px; padding: 12px; }
            .metric-value { margin-top: 8px; font-size: 18px; font-weight: 800; white-space: nowrap; }
            .metric-helper { margin-top: 4px; color: #8b6a52; font-size: 9px; }
            .tables { display: grid; grid-template-columns: 1.05fr .95fr; gap: 12px; margin-top: 12px; align-items: start; }
            .table-card { overflow: hidden; }
            .table-heading { padding: 12px 12px 8px; }
            h2 { margin: 0; font-size: 13px; }
            .table-subtitle { margin: 4px 0 0; color: #7a5c46; font-size: 9px; }
            table { width: 100%; border-collapse: collapse; font-size: 9px; }
            thead { display: table-header-group; }
            tr { break-inside: avoid; page-break-inside: avoid; }
            th, td { padding: 7px 8px; border-top: 1px solid #f2e6d7; text-align: left; }
            th { color: #8b6a52; font-size: 8px; text-transform: uppercase; }
            .num { text-align: right; white-space: nowrap; }
            .empty { padding: 18px; color: #8b6a52; text-align: center; }
            .clients { margin-top: 12px; }
            .footer { display: flex; justify-content: space-between; margin-top: 12px; color: #7a5c46; font-size: 8px; }
            @page { size: A4 landscape; margin: 8mm; }
            @media print { html, body { background: #fff; } body { padding: 0; } .report { max-width: none; border-radius: 0; } }
          </style>
        </head>
        <body>
          <main class="report">
            <header class="header">
              <h1>Visão geral comercial</h1>
              <div class="subtitle">KPIs gerais do eGestor, sem misturar informações da aba Financeiro</div>
            </header>
            <div class="content">
              <section class="filters">
                <div class="filter"><div class="filter-label">Data inicial</div><div class="filter-value">${escapePrintText(formatDateBR(overviewFilters.dtIni))}</div></div>
                <div class="filter"><div class="filter-label">Data final</div><div class="filter-value">${escapePrintText(formatDateBR(overviewFilters.dtFim))}</div></div>
                <div class="filter"><div class="filter-label">Empresa</div><div class="filter-value">${escapePrintText(companyLabel)}</div></div>
                <div class="filter"><div class="filter-label">Limite de pedidos</div><div class="filter-value">${escapePrintText(overviewFilters.limit || "---")}</div></div>
              </section>
              <section class="metrics">${metricCards}</section>
              <section class="tables">
                <article class="table-card">
                  <div class="table-heading"><h2>Qtd. produto por sabor</h2><p class="table-subtitle">Ranking por quantidade vendida no período selecionado.</p></div>
                  <table><thead><tr><th>Produto / sabor</th><th class="num">Qtd.</th><th class="num">Faturamento</th><th class="num">Preço médio</th></tr></thead><tbody>${productRows}</tbody></table>
                </article>
                <article class="table-card">
                  <div class="table-heading"><h2>Pedidos e faturamento por vendedor</h2><p class="table-subtitle">Resumo consolidado por vendedor do eGestor.</p></div>
                  <table><thead><tr><th>Vendedor</th><th class="num">Pedidos</th><th class="num">Faturamento</th><th class="num">Ticket</th></tr></thead><tbody>${sellerRows}</tbody></table>
                </article>
              </section>
              <article class="table-card clients">
                <div class="table-heading"><h2>Top clientes por faturamento</h2></div>
                <table><thead><tr><th>Cliente</th><th class="num">Pedidos</th><th class="num">Faturamento</th><th class="num">Última compra</th></tr></thead><tbody>${clientRows}</tbody></table>
              </article>
              <footer class="footer"><span>Fonte: eGestor</span><span>Gerado em ${escapePrintText(generatedAt)}</span></footer>
            </div>
          </main>
        </body>
      </html>`);
    reportWindow.document.close();
    reportWindow.focus();
    setTimeout(() => reportWindow.print(), 300);
  };

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        await Promise.all([loadSnapshot(), loadOverview()]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const comercialSummary = snapshot?.dashboards?.comercial?.summary || {};
  const promotorSummary = snapshot?.dashboards?.promotor?.summary || {};
  const combustivelSummary = snapshot?.dashboards?.combustivel?.summary || {};
  const overviewSummary = overview?.summary || {};

  const cards = useMemo(() => ([
    { label: "Vendedores ativos", value: formatNumber(comercialSummary?.vendedoresAtivos || 0), helper: "Módulo Comercial", icon: "Users" },
    { label: "Rotas ativas", value: formatNumber(comercialSummary?.rotasAtivas || 0), helper: "Mercos + manuais", icon: "Route" },
    { label: "Lojas ativas", value: formatNumber(promotorSummary?.lojasAtivas || 0), helper: "Promotor de vendas", icon: "Store" },
    { label: "Cadastros combustível", value: formatNumber(combustivelSummary?.cadastrosAtivos || 0), helper: "Pilotos/motos", icon: "Fuel" },
    { label: "Pedidos feitos", value: formatNumber(overviewSummary?.quantidadePedidos || 0), helper: "Comercial eGestor", icon: "ShoppingCart" },
    { label: "Faturamento total", value: formatCurrency(overviewSummary?.faturamentoTotal || 0), helper: "Pedidos válidos no período", icon: "Wallet" },
    { label: "Clientes ativos", value: formatNumber(overviewSummary?.clientesAtivos45Dias || 0), helper: "Últimos 45 dias", icon: "UserCheck" },
    { label: "Ticket médio", value: formatCurrency(overviewSummary?.ticketMedioCompra || 0), helper: "Faturamento / pedidos", icon: "BadgeDollarSign" },
  ]), [comercialSummary, promotorSummary, combustivelSummary, overviewSummary]);

  if (loading) {
    return <PageShell><div className="mx-auto max-w-[1600px] py-16 text-center text-[#7a5c46]">Carregando visão geral...</div></PageShell>;
  }

  return (
    <PageShell>
      <div className="mx-auto flex max-w-[1600px] flex-col gap-6">
        <section className="rounded-[28px] border border-[#ead8c2] bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-[#3b1d0f]">Visão Geral</h1>
              <p className="mt-2 text-sm text-[#7a5c46]">Resumo do projeto inteiro, com atalhos dos módulos e os principais indicadores do sistema.</p>
            </div>
            <Button variant="outline" iconName="RefreshCw" onClick={() => { loadSnapshot(); loadOverview(); }}>Atualizar tudo</Button>
          </div>
        </section>

        {error ? <div className="rounded-2xl border border-[#f8c9c9] bg-[#fff1f1] px-4 py-3 text-sm text-[#b42318]">{error}</div> : null}

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {cards.map((card) => <MetricCard key={card.label} {...card} />)}
        </div>

        <Panel title="Visão geral comercial" subtitle="KPIs gerais do eGestor, sem misturar informações da aba Financeiro" icon="BarChart3">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
            <Input label="Data inicial" type="date" value={overviewFilters.dtIni} onChange={(e) => setOverviewFilters((prev) => ({ ...prev, dtIni: e.target.value }))} />
            <Input label="Data final" type="date" value={overviewFilters.dtFim} onChange={(e) => setOverviewFilters((prev) => ({ ...prev, dtFim: e.target.value }))} />
            <Select label="Empresa" value={overviewFilters.base} onChange={(value) => setOverviewFilters((prev) => ({ ...prev, base: value }))} options={baseFilterOptions} />
            <Input label="Limite de pedidos" type="number" value={overviewFilters.limit} onChange={(e) => setOverviewFilters((prev) => ({ ...prev, limit: e.target.value }))} />
            <div className="flex items-end">
              <Button className="w-full" loading={overviewLoading} onClick={loadOverview} iconName="RefreshCw">Atualizar KPIs</Button>
            </div>
            <div className="flex items-end">
              <Button className="w-full" variant="outline" disabled={overviewLoading} onClick={generateOverviewReport} iconName="FileDown">Gerar relatório</Button>
            </div>
          </div>

          {overviewError ? <div className="mt-4 rounded-2xl border border-[#f8c9c9] bg-[#fff1f1] px-4 py-3 text-sm text-[#b42318]">{overviewError}</div> : null}

          <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-6">
            <MetricCard label="Produtos vendidos" value={formatNumber(overviewSummary?.produtosVendidosTotal || 0)} helper="Quantidade total vendida" icon="Boxes" />
            <MetricCard label="Pedidos feitos" value={formatNumber(overviewSummary?.quantidadePedidos || 0)} helper="Pedidos válidos no período" icon="ShoppingCart" />
            <MetricCard label="Faturamento" value={formatCurrency(overviewSummary?.faturamentoTotal || 0)} helper="Sem cancelados" icon="Wallet" />
            <MetricCard label="Clientes ativos" value={formatNumber(overviewSummary?.clientesAtivos45Dias || 0)} helper="Compraram nos últimos 45 dias" icon="UserCheck" />
            <MetricCard label="Clientes positivados" value={formatNumber(overviewSummary?.clientesPositivadosMes || 0)} helper="Compraram no período" icon="Users" />
            <MetricCard label="Ticket médio" value={formatCurrency(overviewSummary?.ticketMedioCompra || 0)} helper="Faturamento / pedidos" icon="BadgeDollarSign" />
          </div>

          <div className="mt-5 grid gap-6 xl:grid-cols-2">
            <div className="rounded-2xl border border-[#f0ddc7] bg-[#fffaf4] p-4">
              <h3 className="mb-3 font-semibold text-[#3b1d0f]">Qtd. produto por sabor</h3>
              <p className="mb-3 text-xs text-[#7a5c46]">Ranking por quantidade vendida no período selecionado.</p>
              <div className="max-h-[420px] overflow-y-auto pr-2">
                <SimpleTable
                  rows={overview.productSummary || []}
                  columns={[
                    { key: "produto", label: "Produto / sabor" },
                    { key: "quantidade", label: "Qtd.", render: (row) => formatNumber(row.quantidade) },
                    { key: "valorTotal", label: "Faturamento", render: (row) => formatCurrency(row.valorTotal) },
                    { key: "precoMedio", label: "Preço médio", render: (row) => formatCurrency(getAveragePrice(row)) },
                  ]}
                  emptyMessage={overviewLoading ? "Buscando produtos..." : "Nenhum produto vendido no período."}
                />
              </div>
            </div>

            <div className="rounded-2xl border border-[#f0ddc7] bg-[#fffaf4] p-4">
              <h3 className="mb-3 font-semibold text-[#3b1d0f]">Pedidos e faturamento por vendedor</h3>
              <p className="mb-3 text-xs text-[#7a5c46]">Resumo por vendedor do eGestor.</p>
              <div className="max-h-[420px] overflow-y-auto pr-2">
                <SimpleTable
                  rows={overview.sellerSummary || []}
                  columns={[
                    { key: "vendedor", label: "Vendedor" },
                    { key: "pedidos", label: "Pedidos", render: (row) => formatNumber(row.pedidos) },
                    { key: "faturamento", label: "Faturamento", render: (row) => formatCurrency(row.faturamento) },
                    { key: "ticketMedio", label: "Ticket", render: (row) => formatCurrency(row.ticketMedio) },
                  ]}
                  emptyMessage={overviewLoading ? "Buscando vendedores..." : "Nenhum vendedor encontrado no período."}
                />
              </div>
            </div>
          </div>

          <div className="mt-5 rounded-2xl border border-[#f0ddc7] bg-[#fffaf4] p-4">
            <h3 className="mb-3 font-semibold text-[#3b1d0f]">Top clientes por faturamento</h3>
            <SimpleTable
              rows={overview.topClients || []}
              columns={[
                { key: "cliente", label: "Cliente" },
                { key: "pedidos", label: "Pedidos", render: (row) => formatNumber(row.pedidos) },
                { key: "faturamento", label: "Faturamento", render: (row) => formatCurrency(row.faturamento) },
                { key: "ultimaCompra", label: "Última compra", render: (row) => formatDateBR(row.ultimaCompra) },
              ]}
              emptyMessage={overviewLoading ? "Buscando clientes..." : "Nenhum cliente encontrado no período."}
            />
          </div>
        </Panel>
      </div>
    </PageShell>
  );
}
